// utils/useConfiguratorData.ts
function sizeGeometryFromGraphql(size: NonNullable<Variant['size']>) {
    const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0 }
    const trimW = Math.round(num(size.cmWidth) * 10)
    const trimH = Math.round(num(size.cmHeight) * 10)
    const bleedMm = num(size.bleedMm)
    const shape = String(size.shape || '').toLowerCase()
    if (!shape && !bleedMm && !num(size.safeMarginMm)) return null
    return {
        shape: shape || 'rect',
        cornerRadiusMm: num(size.cornerRadiusMm),
        bleedMm,
        safeMarginMm: num(size.safeMarginMm),
        dpi: num(size.dpiTarget) || 300,
        trimWidthMm: trimW,
        trimHeightMm: trimH,
        fullWidthMm: trimW + 2 * bleedMm,
        fullHeightMm: trimH + 2 * bleedMm,
        shapeParams: size.shapeParams ?? null,
    }
}

type Edge<T> = { node: T | null } | null
type Cursor<T> = { edges?: Array<Edge<T>> | null } | null

type VariantGroup = {
    id: string
    title: string
    isSize: boolean
    showInTile?: boolean | null
    icon?: string | null // ✅ optional
}

type Variant = {
    id: string
    title: string | null
    price: number | null
    priceSpecial: number | null
    isDefault: boolean
    isFormula: boolean
    formula: string | null
    size: {
        id: string
        title: string
        cmWidth?: string | number | null
        cmHeight?: string | number | null
        shape?: string | null
        cornerRadiusMm?: string | number | null
        bleedMm?: string | number | null
        safeMarginMm?: string | number | null
        dpiTarget?: number | null
        shapeParams?: Record<string, unknown> | null
    } | null
    variantGroup: { id: string }

    // ✅ optional (falls du es im GQL wirklich hast)
    isFeatured?: boolean | null
    thumbnail?: string | null
    isCrop?: boolean | null
}

type VariantGroupText = {
    id: string
    description: string | null // ✅ safer
    variantGroup: { id: string }
}

type Product = {
    id: string
    title: string
    price: number | null
    priceSpecial: number | null
    hasIndividualSizes?: boolean | null
    minSizeX?: number | null
    minSizeY?: number | null
    maxSizeX?: number | null
    maxSizeY?: number | null
    variantGroups: Cursor<VariantGroup>
    variants: Cursor<Variant>
    variantGroupTexts?: Cursor<VariantGroupText>
}

// ---- Helpers
const lastId = (iri: string) => iri?.split('/').pop() ?? iri

const centsToEuro = (v?: number | null) =>
    typeof v === 'number' && Number.isFinite(v) ? v / 100 : 0

const toBool = (v: unknown): boolean => {
    if (typeof v === 'boolean') return v
    if (typeof v === 'number') return v === 1
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        if (s === '1' || s === 'true' || s === 'yes') return true
        if (s === '0' || s === 'false' || s === 'no' || s === '') return false
    }
    return false
}

const effectivePriceCents = (v: Variant) =>
    (typeof v.priceSpecial === 'number' && v.priceSpecial > 0 ? v.priceSpecial : v.price) ?? 0

/** Normalpreis in Cent, nur wenn ein niedrigerer Aktionspreis greift. */
const regularPriceCents = (v: Variant): number | null =>
    typeof v.priceSpecial === 'number' && v.priceSpecial > 0 && typeof v.price === 'number' && v.priceSpecial < v.price
        ? v.price
        : null

const getEdges = <T>(c?: Cursor<T> | null): T[] =>
    (c?.edges?.map(e => e?.node).filter(Boolean) as T[]) ?? []

function normalizeUrl(src: string, base = 'https://www.pixelfoto-express.de') {
    if (!src) return ''
    return src.startsWith('http') ? src : `${base}${src}`
}

// ---- Main mapper
export function mapConfiguratorData(product: Product, opts?: { placeholderIcon?: string; apiBase?: string }) {
    const apiBase = opts?.apiBase ?? 'https://www.pixelfoto-express.de'
    const ph = opts?.placeholderIcon ?? ''

    const groups = getEdges(product.variantGroups)
    const variants = getEdges(product.variants)
    const texts = getEdges(product.variantGroupTexts)

    // Map: groupId -> description
    const textByGroupId = new Map<string, string>()
    texts.forEach(t => {
        const gid = lastId(t.variantGroup.id)
        if (t?.description) textByGroupId.set(gid, t.description)
    })

    // Map: groupId -> variants[]
    const variantsByGroup = new Map<string, Variant[]>()
    variants.forEach(v => {
        const gid = lastId(v.variantGroup.id)
        const arr = variantsByGroup.get(gid) ?? []
        arr.push(v)
        variantsByGroup.set(gid, arr)
    })

    function getGroupIcon(g?: VariantGroup | null) {
        const src = g?.icon || ''
        return src ? normalizeUrl(src, apiBase) : ph
    }

    const displayGroups = groups.map(g => {
        const gid = lastId(g.id)
        const description = textByGroupId.get(gid) || ''
        const vlist = variantsByGroup.get(gid) ?? []

        if (g.isSize) {
            const seen = new Set<string>()
            const sizeOptions = vlist
                .filter(v => v.size?.id)
                .map(v => ({
                    id: lastId(v.size!.id),
                    title: v.size!.title,
                    priceCents: effectivePriceCents(v),
                    price: centsToEuro(effectivePriceCents(v)),
                    regularPriceCents: regularPriceCents(v),
                    regularPrice: regularPriceCents(v) !== null ? centsToEuro(regularPriceCents(v)) : null,
                    featured: !!v.isFeatured,
                    isDefault: !!v.isDefault,
                    variantId: lastId(v.id),
                    widthCm: v.size!.cmWidth ?? null,
                    heightCm: v.size!.cmHeight ?? null,
                    // Geometrie nur aus Size-Feldern (GraphQL-Pfad). Produkt-/Kategorie-
                    // Fallbacks kennt nur der Bootstrap (ProductGeometryResolver im Backend).
                    geometry: sizeGeometryFromGraphql(v.size!),
                }))
                .filter(opt => {
                    if (seen.has(opt.id)) return false
                    seen.add(opt.id)
                    return true
                })

            const def = sizeOptions.find(o => o.isDefault) || sizeOptions[0] || null

            return {
                id: gid,
                title: g.title,
                icon: getGroupIcon(g),
                isSize: true as const,
                description,
                sizeOptions,
                selected: def
                    ? { id: def.id, title: def.title, price: def.price, priceCents: def.priceCents }
                    : null,
            }
        }

        const options = vlist.map(v => ({
            id: lastId(v.id),
            title: v.title || '',
            priceCents: effectivePriceCents(v),
            price: centsToEuro(effectivePriceCents(v)),
            regularPriceCents: regularPriceCents(v),
            regularPrice: regularPriceCents(v) !== null ? centsToEuro(regularPriceCents(v)) : null,
            isFormula: !!v.isFormula,
            isCrop: v.isCrop !== false,
            thumbnail: v.thumbnail ? normalizeUrl(v.thumbnail, apiBase) : null,
            formula: v.formula || null,
            isDefault: !!v.isDefault,
        }))

        const def = options.find(o => o.isDefault) || options[0] || null

        return {
            id: gid,
            title: g.title,
            icon: getGroupIcon(g),
            isSize: false as const,
            showInTile: toBool(g.showInTile),
            description,
            options,
            selected: def
                ? { id: def.id, title: def.title, price: def.price, priceCents: def.priceCents }
                : null,
        }
    })

    const basePrice =
        centsToEuro(product.priceSpecial ?? undefined) ||
        centsToEuro(product.price ?? undefined)

    return {
        product: {
            id: lastId(product.id),
            title: product.title,
            basePrice,
            hasIndividualSizes: product.hasIndividualSizes === true,
            minSizeX: product.minSizeX ?? null,
            minSizeY: product.minSizeY ?? null,
            maxSizeX: product.maxSizeX ?? null,
            maxSizeY: product.maxSizeY ?? null,
        },
        groups: displayGroups,
    }
}
